import type { ReactNode } from 'react';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';
import type { FragmentType } from '@e-socna/data-layer';

import { Tabs, TabsStrategy, TabsVariant } from '../../../components/Tabs';
import { useBuildPaths } from '../../../hooks/useBuildPaths.hook';

import { Account } from './Account';
import { Authorizations } from './Authorizations';
import { Admin } from './Admin';
import { Settings } from './Settings';

export type UserPaths = Readonly<{
  account: string;
  admin?: string;
  adminEdit?: string;
  authorizations: string;
  authorizationsEdit?: string;
  settings: string;
}>;

export const UserPage_Tab_User_Fragment = graphql(`
  fragment UserPage_Tab_User_Fragment on User {
    ...UserPage_Authorizations_User_Fragment
    ...UserPage_Account_User_Fragment
    ...UserPage_Admin_User_Fragment
    ...UserPage_Settings_User_Fragment
  }
`);

interface Props {
  readonly user: FragmentType<typeof UserPage_Tab_User_Fragment>;
  readonly paths: UserPaths;
  // Actions of the account tab, depending on who looks at the user.
  readonly accountActions?: ReactNode;
}

export const User = ({
  user: pUser,
  paths,
  accountActions,
}: Readonly<Props>) => {
  const user = getFragmentData(UserPage_Tab_User_Fragment, pUser);

  const { getActiveTab } = useBuildPaths(paths, EMPTY_OBJECT, {
    account: 'account',
    admin: 'admin',
    adminEdit: 'admin',
    authorizations: 'authorizations',
    authorizationsEdit: 'authorizations',
    settings: 'settings',
  });

  return (
    <Tabs
      activeTab={getActiveTab()}
      id="user-tabs"
      strategy={TabsStrategy.Controlled}
      tabs={[
        {
          key: 'account',
          label: 'Compte',
          panel: <Account actions={accountActions} user={user} />,
          to: paths.account,
          value: 'account',
        },
        {
          key: 'authorizations',
          label: 'Autorisations',
          panel: (
            <Authorizations
              paths={{
                consult: paths.authorizations,
                edit: paths.authorizationsEdit,
              }}
              user={user}
            />
          ),
          to: paths.authorizations,
          value: 'authorizations',
        },
        {
          key: 'settings',
          label: 'Réglages',
          panel: <Settings user={user} />,
          to: paths.settings,
          value: 'settings',
        },
        {
          key: 'admin',
          label: 'Administration',
          panel: (
            <Admin
              paths={{
                consult: paths.admin ?? EMPTY_STRING,
                edit: paths.adminEdit,
              }}
              user={user}
            />
          ),
          to: paths.admin ?? EMPTY_STRING,
          value: 'admin',
        },
      ].filter(
        (tab) => Object.keys(paths).findIndex((key) => key === tab.key) > -1,
      )}
      title="Onglets de l'écran de Fiche d'un Compte Utilisateur"
      variant={TabsVariant.Vertical}
    />
  );
};
